import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, CheckCircle, Wrench, FileText, ShoppingCart } from "@phosphor-icons/react";

export const BUCKET_META = {
  delivered: { label: "Fulfilled & Delivered", color: "#16a34a", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle },
  preparation: { label: "In Preparation", color: "#1E7FD4", bg: "bg-blue-50 border-blue-200", icon: Wrench },
  awaiting_awb: { label: "Waiting for AWB Approval", color: "#d97706", bg: "bg-amber-50 border-amber-200", icon: FileText },
  collection: { label: "In Product Collection", color: "#7c3aed", bg: "bg-violet-50 border-violet-200", icon: ShoppingCart },
};

const Donut = ({ pct, segments }) => {
  const r = 52, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 128 128" className="w-36 h-36 shrink-0" role="img" aria-label={`Fulfilment ${pct}%`} data-testid="fulfillment-donut">
      <circle cx="64" cy="64" r={r} fill="none" stroke="#e5e7eb" strokeWidth="14" />
      {segments.map((s) => {
        const len = (s.pct / 100) * c;
        const el = <circle key={s.key} cx="64" cy="64" r={r} fill="none" stroke={s.color} strokeWidth="14" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} transform="rotate(-90 64 64)" style={{ transition: "stroke-dasharray .6s ease" }} />;
        offset += len;
        return el;
      })}
      <text x="64" y="60" textAnchor="middle" style={{ fontSize: 22, fontWeight: 700, fill: "#111", fontFamily: "inherit" }} data-testid="fulfillment-pct">{`${pct}%`}</text>
      <text x="64" y="78" textAnchor="middle" style={{ fontSize: 9, fill: "#666", letterSpacing: 1 }}>FULFILLED</text>
    </svg>
  );
};

export const FulfillmentOverview = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/stats/fulfillment").then((r) => setD(r.data)).catch(() => {}); }, [refreshKey]);
  useEffect(() => {
    const onFocus = () => api.get("/stats/fulfillment").then((r) => setD(r.data)).catch(() => {});
    window.addEventListener("focus", onFocus);
    const iv = setInterval(onFocus, 60000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(iv); };
  }, []);
  if (!d) return null;
  const order = ["delivered", "preparation", "awaiting_awb", "collection"];
  const segments = order.map((k) => ({ key: k, pct: d.buckets[k].percentage, color: BUCKET_META[k].color }));
  const go = (bucket) => navigate(bucket ? `/orders?fulfil=${bucket}` : "/orders");
  return (
    <div className="hp-card p-6" data-testid="fulfillment-overview">
      <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2 mb-5"><Package size={20} className="text-primary" weight="duotone" /> Order Fulfillment Overview</h2>
      <div className="flex flex-col md:flex-row gap-6 items-center">
        <Donut pct={d.fulfillment_percentage} segments={segments} />
        <div className="flex-1 w-full space-y-3">
          <div className="w-full h-3 rounded-full bg-muted overflow-hidden" data-testid="fulfillment-bar">
            <div className="h-full rounded-full bg-emerald-600 transition-[width] duration-700" style={{ width: `${d.fulfillment_percentage}%` }} />
          </div>
          <p className="text-sm text-muted-foreground" data-testid="fulfillment-formula">{d.buckets.delivered.count} fulfilled &amp; delivered ÷ {d.total_orders} total orders = <strong className="text-foreground">{d.fulfillment_percentage}%</strong></p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <button onClick={() => go(null)} className="text-left rounded-md border border-border bg-muted/30 p-3 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]" data-testid="fulfil-card-total">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Total Orders</p>
              <p className="font-heading text-2xl font-bold mt-1">{d.total_orders}</p>
              <p className="text-xs text-muted-foreground">100%</p>
            </button>
            {order.map((k) => { const m = BUCKET_META[k], b = d.buckets[k], I = m.icon; return (
              <button key={k} onClick={() => go(k)} className={`text-left rounded-md border p-3 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] ${m.bg}`} data-testid={`fulfil-card-${k}`}>
                <p className="text-[11px] uppercase tracking-wide flex items-center gap-1" style={{ color: m.color }}><I size={13} weight="bold" /> {m.label}</p>
                <p className="font-heading text-2xl font-bold mt-1">{b.count}</p>
                <p className="text-xs text-muted-foreground">{b.percentage}% of orders</p>
              </button>
            ); })}
          </div>
        </div>
      </div>
    </div>
  );
};
